import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/BomManagement.css';

const BomManagement = () => {
  const [products, setProducts] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    category: '',
    productType: 'base',
    hasQuantitySelector: false,
    maxQuantity: 1,
    laborHours: 0
  });

  // BOM modal
  const [showBomModal, setShowBomModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [bomItems, setBomItems] = useState([]);
  const [bomSearch, setBomSearch] = useState('');
  const [bomTableSearch, setBomTableSearch] = useState('');
  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [partQuantity, setPartQuantity] = useState(1.0);
  const [selectedPart, setSelectedPart] = useState(null);

  // Duplicate modal
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicatingProduct, setDuplicatingProduct] = useState(null);
  // Import result modal
  const [showImportResult, setShowImportResult] = useState(false);
  const [importResult, setImportResult] = useState({ imported: 0, skipped: 0, notFound: [] });
  const [laborCostPerHour, setLaborCostPerHour] = useState(150);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchParts();
    fetchSettings();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParts = async () => {
    try {
      const response = await api.get('/parts');
      setParts(response.data);
    } catch (error) {
      console.error('Error fetching parts:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      if (response.data.laborCostPerHour) {
        setLaborCostPerHour(response.data.laborCostPerHour);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBom = async (productId) => {
    try {
      const response = await api.get(`/boms/${productId}`);
      setBomItems(response.data);
    } catch (error) {
      console.error('Error fetching BOM:', error);
    }
  };

  // Product handlers
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct._id}`, productForm);
      } else {
        await api.post('/products', productForm);
      }
      setShowProductModal(false);
      setEditingProduct(null);
      setProductForm({
        name: '',
        description: '',
        category: '',
        productType: 'base',
        hasQuantitySelector: false,
        maxQuantity: 1,
        laborHours: 0
      });
      fetchProducts();
    } catch (error) {
      alert(error.response?.data?.message || 'שגיאה בשמירה');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || '',
      category: product.category || '',
      productType: product.productType || 'base',
      hasQuantitySelector: product.hasQuantitySelector || false,
      maxQuantity: product.maxQuantity || 1,
      laborHours: product.laborHours || 0
    });
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('האם למחוק מוצר זה?')) {
      try {
        await api.delete(`/products/${id}`);
        fetchProducts();
      } catch (error) {
        alert('שגיאה במחיקה');
      }
    }
  };

  // BOM handlers
  const handleOpenBom = async (product) => {
    setSelectedProduct(product);
    await fetchBom(product._id);
    setShowBomModal(true);
  };

  const handleAddPartToBom = async () => {
    if (!selectedPart) return;
    try {
      await api.post('/boms', {
        product: selectedProduct._id,
        part: selectedPart._id,
        quantity: partQuantity
      });
      await fetchBom(selectedProduct._id);
      setShowAddPartModal(false);
      setSelectedPart(null);
      setPartQuantity(1);
      setBomSearch('');
    } catch (error) {
      alert(error.response?.data?.message || 'שגיאה בהוספה');
    }
  };

  const handleUpdateBomQuantity = async (bomItemId, newQuantity) => {
    try {
      await api.put(`/boms/${bomItemId}`, { quantity: newQuantity });
      await fetchBom(selectedProduct._id);
    } catch (error) {
      alert('שגיאה בעדכון');
    }
  };

  const handleRemoveFromBom = async (bomItemId) => {
    if (window.confirm('האם להסיר פריט זה מה-BOM?')) {
      try {
        await api.delete(`/boms/${bomItemId}`);
        await fetchBom(selectedProduct._id);
      } catch (error) {
        alert('שגיאה במחיקה');
      }
    }
  };

  // Duplicate handlers
  const handleOpenDuplicate = (product) => {
    setDuplicatingProduct(product);
    setDuplicateName(`${product.name} - העתק`);
    setShowDuplicateModal(true);
  };

  const handleDuplicate = async () => {
    try {
      await api.post(`/products/${duplicatingProduct._id}/duplicate`, {
        newName: duplicateName
      });
      setShowDuplicateModal(false);
      setDuplicatingProduct(null);
      setDuplicateName('');
      fetchProducts();
      alert('המוצר שוכפל בהצלחה');
    } catch (error) {
      alert(error.response?.data?.message || 'שגיאה בשכפול');
    }
  };

  // Calculate BOM costs
  const calculatePartsCost = () => {
    return bomItems.reduce((total, item) => {
      return total + (item.part?.cost || 0) * item.quantity;
    }, 0);
  };

  const calculateLaborCost = () => {
    return (selectedProduct?.laborHours || 0) * laborCostPerHour;
  };

  const calculateTotalCost = () => {
    return calculatePartsCost() + calculateLaborCost();
  };

  // Export BOM to CSV
  const handleExportBomCsv = () => {
    if (!selectedProduct || bomItems.length === 0) return;

    // CSV Header
    let csv = '\ufeff'; // BOM for Hebrew support
    csv += 'מק"ט,שם פריט,שם באנגלית,עלות יחידה,כמות,סה"כ\n';

    // BOM Items
    bomItems.forEach(item => {
      const part = item.part;
      if (part) {
        const sku = part.sku || '';
        const nameHe = part.nameHe || '';
        const nameEn = part.nameEn || '';
        const cost = part.cost?.toFixed(2) || '0';
        const quantity = item.quantity || 1;
        const total = ((part.cost || 0) * quantity).toFixed(2);
        
        csv += `"${sku}","${nameHe}","${nameEn}",${cost},${quantity},${total}\n`;
      }
    });

    // Summary
    csv += '\n';
    csv += `"","עלות חלקים","","${calculatePartsCost().toFixed(2)}","",""\n`;
    csv += `"","עלות עבודה (${selectedProduct.laborHours || 0} שעות)","","${calculateLaborCost().toFixed(2)}","",""\n`;
    csv += `"","סה״כ עלות","","${calculateTotalCost().toFixed(2)}","",""\n`;

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `BOM_${selectedProduct.name}_${new Date().toLocaleDateString('he-IL')}.csv`;
    link.click();
  };

// Import BOM from CSV
  const handleImportBomCsv = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedProduct) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const rows = text.split('\n').filter(row => row.trim());
        
        const notFound = [];
        const toImport = [];
        
        // Skip header row
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          
          // Skip empty rows and summary rows
          if (!row.trim() || row.includes('עלות חלקים') || row.includes('עלות עבודה') || row.includes('סה״כ עלות')) continue;
          
          // Parse CSV - handle quoted values
          const values = [];
          let current = '';
          let inQuotes = false;
          
          for (let j = 0; j < row.length; j++) {
            const char = row[j];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim()); // Last value
          
          // Get SKU (first column) and quantity (5th column)
          const sku = values[0]?.replace(/"/g, '').trim();
          const quantity = parseFloat(values[4]) || 1;
          
          if (sku && sku !== '' && sku !== 'מק"ט') {
            // Find part by SKU
            const part = parts.find(p => p.sku === sku);
            if (part) {
              toImport.push({ partId: part._id, sku: sku, quantity });
            } else {
              notFound.push(sku);
            }
          }
        }



        // Import found items
        if (toImport.length > 0) {
          let imported = 0;
          let skipped = 0;
          
          for (const item of toImport) {
            // Check if already exists in BOM
            const exists = bomItems.some(b => b.part?._id === item.partId);
            if (!exists) {
              try {
                await api.post('/boms', {
                  product: selectedProduct._id,
                  part: item.partId,
                  quantity: item.quantity
                });
                imported++;
              } catch (err) {
                console.error('Error importing item:', err);
              }
            } else {
              skipped++;
            }
          }
          
          await fetchBom(selectedProduct._id);
          
          setImportResult({ imported, skipped, notFound });
          setShowImportResult(true);
        } else if (notFound.length === 0) {
          alert('לא נמצאו פריטים לייבוא');
        } else {
          setImportResult({ imported: 0, skipped: 0, notFound });
          setShowImportResult(true);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert('שגיאה בייבוא: ' + error.message);
      }
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };
  // Filtered lists
  const filteredProducts = products.filter(product =>
    product.name.includes(search) ||
    (product.category && product.category.includes(search))
  );
const filteredBomItems = bomItems.filter(item =>
    item.part?.sku?.includes(bomTableSearch) ||
    item.part?.nameHe?.includes(bomTableSearch) ||
    (item.part?.nameEn && item.part?.nameEn.toLowerCase().includes(bomTableSearch.toLowerCase()))
  );
  const filteredPartsForAdd = parts.filter(part =>
    !bomItems.some(item => item.part?._id === part._id) &&
    (part.sku.includes(bomSearch) ||
     part.nameHe.includes(bomSearch) ||
     (part.nameEn && part.nameEn.toLowerCase().includes(bomSearch.toLowerCase())))
  );

  return (
    <div className="bom-management">
      <div className="page-header">
        <h1>ניהול BOMs</h1>
      </div>

      <div className="toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder="חיפוש מוצר..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="btn-primary" onClick={() => {
          setEditingProduct(null);
          setProductForm({
            name: '',
            description: '',
            category: '',
            productType: 'base',
            hasQuantitySelector: false,
            maxQuantity: 1,
            laborHours: 0
          });
          setShowProductModal(true);
        }}>
          + מוצר חדש
        </button>
      </div>

      {loading ? (
        <div className="loading">טוען...</div>
      ) : (
        <div className="products-grid">
          {filteredProducts.map(product => (
            <div key={product._id} className="product-card">
              <div className="product-header">
                <h3>{product.name}</h3>
                <span className={`product-type ${product.productType}`}>
                  {product.productType === 'base' ? 'בסיס' : 'תוספת'}
                </span>
              </div>
              {product.description && <p className="product-desc">{product.description}</p>}
              {product.category && <span className="product-category">{product.category}</span>}
              <div className="product-actions">
                <button className="btn-bom" onClick={() => handleOpenBom(product)}>
                  ניהול BOM
                </button>
                <button className="btn-duplicate" onClick={() => handleOpenDuplicate(product)}>
                  שכפול
                </button>
                <button className="btn-edit-small" onClick={() => handleEditProduct(product)}>
                  ערוך
                </button>
                <button className="btn-delete-small" onClick={() => handleDeleteProduct(product._id)}>
                  מחק
                </button>
              </div>
            </div>
          ))}
          {filteredProducts.length === 0 && (
            <div className="no-data">לא נמצאו מוצרים</div>
          )}
        </div>
      )}

      {/* Product Modal */}
      {showProductModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingProduct ? 'עריכת מוצר' : 'מוצר חדש'}</h2>
            <form onSubmit={handleProductSubmit}>
              <div className="form-group">
                <label>שם המוצר</label>
                <input
                  type="text"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>תיאור</label>
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>קטגוריה</label>
                <select
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  required
                >
                  <option value="">בחר קטגוריה...</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>סוג מוצר</label>
                <select
                  value={productForm.productType}
                  onChange={(e) => setProductForm({ ...productForm, productType: e.target.value })}
                >
                  <option value="base">מוצר בסיס (עגלה)</option>
                  <option value="addon">תוספת</option>
                </select>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={productForm.hasQuantitySelector}
                    onChange={(e) => setProductForm({ ...productForm, hasQuantitySelector: e.target.checked })}
                  />
                  אפשר בחירת כמות
                </label>
              </div>
              {productForm.hasQuantitySelector && (
                <div className="form-group">
                  <label>כמות מקסימלית</label>
                  <input
                    type="number"
                    min="1"
                    value={productForm.maxQuantity}
                    onChange={(e) => setProductForm({ ...productForm, maxQuantity: parseInt(e.target.value) })}
                  />
                </div>
              )}
              <div className="form-group">
                <label>שעות עבודה</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={productForm.laborHours}
                  onChange={(e) => setProductForm({ ...productForm, laborHours: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="modal-buttons">
                <button type="submit" className="btn-primary">שמור</button>
                <button type="button" className="btn-cancel" onClick={() => setShowProductModal(false)}>ביטול</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BOM Modal */}
      {showBomModal && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="bom-modal-header">
              <h2>BOM - {selectedProduct.name}</h2>
            </div>
            
            <div className="bom-summary">
              <div className="bom-summary-row">
                <span>עלות חלקים:</span>
                <strong>₪{calculatePartsCost().toFixed(2)}</strong>
              </div>
              <div className="bom-summary-row">
                <span>עלות עבודה ({selectedProduct.laborHours || 0} שעות × ₪{laborCostPerHour}):</span>
                <strong>₪{calculateLaborCost().toFixed(2)}</strong>
              </div>
              <div className="bom-summary-row total">
                <span>סה״כ עלות:</span>
                <strong>₪{calculateTotalCost().toFixed(2)}</strong>
              </div>
            </div>
            
            <div className="bom-toolbar">
              <input
                type="text"
                placeholder="חיפוש בתוך ה-BOM..."
                value={bomTableSearch}
                onChange={(e) => setBomTableSearch(e.target.value)}
                className="bom-search-input"
              />
              <div className="bom-toolbar-buttons">
                <label className="btn-import-csv">
                  ייבוא CSV
                  <input type="file" accept=".csv" onChange={handleImportBomCsv} hidden />
                </label>
                <button className="btn-export-csv" onClick={handleExportBomCsv}>
                  ייצוא CSV
                </button>
                <button className="btn-add-part" onClick={() => setShowAddPartModal(true)}>
                  + הוסף פריט
                </button>
              </div>
            </div>

            <div className="bom-table-container">
              <table className="bom-table">
                <thead>
                  <tr>
                    <th>מק״ט</th>
                    <th>שם פריט</th>
                    <th>עלות יחידה</th>
                    <th>כמות</th>
                    <th>סה״כ</th>
                    <th>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBomItems.map(item => (
                    <tr key={item._id}>
                      <td>{item.part?.sku}</td>
                      <td>{item.part?.nameHe}</td>
                      <td>₪{item.part?.cost?.toFixed(2)}</td>
                      <td>
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={item.quantity}
                          onChange={(e) => handleUpdateBomQuantity(item._id, parseFloat(e.target.value) || 0)}
                          className="quantity-input"
                        />
                      </td>
                      <td>₪{((item.part?.cost || 0) * item.quantity).toFixed(2)}</td>
                      <td>
                        <button className="btn-remove" onClick={() => handleRemoveFromBom(item._id)}>
                          הסר
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bomItems.length === 0 && (
                <div className="no-data">אין פריטים ב-BOM</div>
              )}
              {bomItems.length > 0 && filteredBomItems.length === 0 && (
                <div className="no-data">לא נמצאו פריטים תואמים לחיפוש</div>
              )}
            </div>

            <div className="modal-buttons">
              <button className="btn-cancel" onClick={() => {
                setShowBomModal(false);
                setSelectedProduct(null);
                setBomItems([]);
                setBomTableSearch('');
              }}>סגור</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Part to BOM Modal */}
      {showAddPartModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>הוסף פריט ל-BOM</h2>
            <div className="form-group">
              <label>חיפוש פריט</label>
              <input
                type="text"
                placeholder="חפש לפי מק״ט או שם..."
                value={bomSearch}
                onChange={(e) => setBomSearch(e.target.value)}
              />
            </div>
            
            <div className="parts-select-list">
              {filteredPartsForAdd.slice(0, 10).map(part => (
                <div
                  key={part._id}
                  className={`part-select-item ${selectedPart?._id === part._id ? 'selected' : ''}`}
                  onClick={() => setSelectedPart(part)}
                >
                  <span className="part-sku">{part.sku}</span>
                  <span className="part-name">{part.nameHe}</span>
                  <span className="part-cost">₪{part.cost?.toFixed(2)}</span>
                </div>
              ))}
            </div>

            {selectedPart && (
              <div className="form-group">
                <label>כמות</label>
                <input
                  type="number"
                  min="0.01"
                  step="any"
                  value={partQuantity}
                  onChange={(e) => setPartQuantity(e.target.value === '' ? '' : parseFloat(e.target.value))}
                />
              </div>
            )}

            <div className="modal-buttons">
              <button
                className="btn-primary"
                onClick={handleAddPartToBom}
                disabled={!selectedPart}
              >
                הוסף
              </button>
              <button className="btn-cancel" onClick={() => {
                setShowAddPartModal(false);
                setSelectedPart(null);
                setPartQuantity(1);
                setBomSearch('');
              }}>ביטול</button>
            </div>
          </div>
        </div>
      )}

      {/* Import Result Modal */}
      {showImportResult && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>תוצאות ייבוא</h2>
            
            <div className="import-result-summary">
              {importResult.imported > 0 && (
                <div className="import-result-row success">
                  <span>✓</span>
                  <span>יובאו {importResult.imported} פריטים בהצלחה</span>
                </div>
              )}
              {importResult.skipped > 0 && (
                <div className="import-result-row warning">
                  <span>⚠</span>
                  <span>{importResult.skipped} פריטים כבר קיימים ב-BOM</span>
                </div>
              )}
              {importResult.notFound.length > 0 && (
                <div className="import-result-row error">
                  <span>✗</span>
                  <span>{importResult.notFound.length} מק"טים לא נמצאו במערכת</span>
                </div>
              )}
            </div>

            {importResult.notFound.length > 0 && (
              <div className="import-not-found">
                <label>מק"טים חסרים (אפשר להעתיק):</label>
                <textarea
                  readOnly
                  value={importResult.notFound.join('\n')}
                  rows={Math.min(importResult.notFound.length, 10)}
                />
                <button 
                  className="btn-copy"
                  onClick={(e) => {
                    navigator.clipboard.writeText(importResult.notFound.join('\n'));
                    e.target.textContent = 'הועתק ✓';
                    setTimeout(() => e.target.textContent = 'העתק לקליפבורד', 2000);
                  }}
                >
                  העתק לקליפבורד
                </button>
              </div>
            )}

            <div className="modal-buttons">
              <button className="btn-primary" onClick={() => setShowImportResult(false)}>סגור</button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Modal */}
      {showDuplicateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>שכפול מוצר</h2>
            <p>משכפל את "{duplicatingProduct?.name}" כולל כל ה-BOM שלו</p>
            <div className="form-group">
              <label>שם המוצר החדש</label>
              <input
                type="text"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                required
              />
            </div>
            <div className="modal-buttons">
              <button className="btn-primary" onClick={handleDuplicate}>שכפל</button>
              <button className="btn-cancel" onClick={() => {
                setShowDuplicateModal(false);
                setDuplicatingProduct(null);
                setDuplicateName('');
              }}>ביטול</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BomManagement;